import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, Calendar, Syringe, Save, Loader2, CheckCircle, Clock } from 'lucide-react';
import { Vaccination } from '../../types';
import toast from 'react-hot-toast';
import { todayLocal } from '../../utils/dateUtils';

type AddVaccineModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (vaccineData: Omit<Vaccination, 'id' | 'createdAt' | 'pet'>) => void;
  petId: string;
  isLoading: boolean;
};

const COMMON_VACCINES = [
  'Karma Aşı', 'Kuduz', 'Karma Yıllık', 'İç-Dış Parazit',
  'FELV', 'Bordatella',
];

const AddVaccineModal: React.FC<AddVaccineModalProps> = ({ isOpen, onClose, onSubmit, petId, isLoading }) => {
  const [vaccineName, setVaccineName] = useState('');
  const [date, setDate] = useState(todayLocal());
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'completed' | 'pending'>('completed');
  const [nextDueDate, setNextDueDate] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setVaccineName('');
      setDate(todayLocal());
      setNotes('');
      setStatus('completed');
      setNextDueDate('');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!vaccineName.trim()) {
      toast.error('Aşı adı zorunlu');
      return;
    }
    if (!date) {
      toast.error('Tarih zorunlu');
      return;
    }
    if (!petId) {
      toast.error('Hasta seçilmemiş');
      return;
    }
    // Planlı kayıtta sonraki doz alanı gizli: değeri yok say
    const effectiveNext = status === 'completed' ? nextDueDate : '';
    if (effectiveNext && effectiveNext < date) {
      toast.error('Sonraki doz tarihi aşı tarihinden önce olamaz');
      return;
    }

    // Sonraki doz adını notlara ekle (Vaccinations sayfasıyla tutarlı)
    const notesWithNext = effectiveNext
      ? `${notes ? notes + ' | ' : ''}Sonraki: ${vaccineName}`
      : notes;

    onSubmit({
      pet_id: petId,
      petId: petId,
      name: vaccineName.trim(),
      vaccine_name: vaccineName.trim(),
      date,
      next_due_date: effectiveNext || '',
      notes: notesWithNext,
      status,
    } as any);
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 flex justify-center items-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 220, damping: 22 }}
          onClick={(e) => e.stopPropagation()}
          className="dribbble-panel max-w-md w-full max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0 bg-white rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white shadow-md shadow-purple-300/40">
                <Syringe className="w-4 h-4" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Yeni Aşı Ekle</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">

              {/* Aşı Türü — Durum Seçici */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Durum <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setStatus('completed')}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      status === 'completed'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-emerald-200'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">Yapıldı</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus('pending')}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      status === 'pending'
                        ? 'border-amber-500 bg-amber-50 text-amber-700 font-semibold'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-amber-200'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Planlanan</span>
                  </button>
                </div>
              </div>

              {/* Aşı Adı */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Aşı Adı <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Syringe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={vaccineName}
                    onChange={(e) => setVaccineName(e.target.value)}
                    placeholder="örn: Karma Aşı, Kuduz"
                    className="input pl-10"
                    required
                  />
                </div>
                {/* Hızlı seçim chips */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {COMMON_VACCINES.map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setVaccineName(v)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                        vaccineName === v
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-700'
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tarih */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  {status === 'completed' ? 'Uygulama Tarihi' : 'Planlanan Tarih'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="input pl-10"
                    required
                  />
                </div>
              </div>

              {/* Sonraki Doz (opsiyonel) — sadece "Yapıldı" durumunda göster */}
              {status === 'completed' && (
                <div className="space-y-1.5 bg-amber-50/40 border border-amber-200 rounded-xl p-3">
                  <label className="block text-xs font-bold text-amber-700 uppercase tracking-wide">
                    Sonraki Doz Hatırlatması (Opsiyonel)
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                    <input
                      type="date"
                      value={nextDueDate}
                      min={date || undefined}
                      onChange={(e) => setNextDueDate(e.target.value)}
                      className="w-full rounded-lg border-2 border-amber-200 focus:border-amber-400 focus:ring-4 focus:ring-amber-500/20 outline-none pl-10 pr-3 py-2 text-sm bg-white"
                    />
                  </div>
                  <p className="text-[11px] text-amber-700/80 italic">
                    Boş bırakırsan hatırlatma eklenmez
                  </p>
                </div>
              )}

              {/* Notlar */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Notlar <span className="text-gray-400 font-normal">(Opsiyonel)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Aşı veya hasta hakkında ek bilgiler..."
                  className="input resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 border-t border-gray-200 bg-white shrink-0 rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary flex-1"
                disabled={isLoading}
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isLoading || !vaccineName.trim() || !date}
                className="btn btn-primary flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Kaydet
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default AddVaccineModal;
